import { getServiceUri } from "../metadata/MetadataLoader";
import { HTTP_ERROR_EVENT } from "@auto/events/Gui";


class RestClient {

	constructor() {
		this.oidcService = null;
	}

	setOidcSession = (session) => {
		if (this.oidcService?.setSession) {
			this.oidcService.setSession(session);
		}
	}

	getBearerToken = () => {
		return this.oidcService?.token || null;
	}

	getHeaders = (contentType = true) => {
		const headers = {};

		if (contentType) {
			headers["Content-Type"] = "application/json";
		}

		const token = this.getBearerToken();

		if (token) {
			headers["Authorization"] = "Bearer " + token;
		}

		return headers;
	}

	handleUnauthorized = (response) => {
		if (response.status === 401 && this.oidcService?.login) {
			this.oidcService.login();
			return true;
		}
		return false;
	}

	getService = (serviceUri) => serviceUri ? serviceUri : getServiceUri();

	emitHttpError = async (response, method) => {
		let body = null;
		try {
			body = await response.text();
		} catch (_) {}

		HTTP_ERROR_EVENT.publish({
			status: response.status,
			statusText: response.statusText,
			url: response.url,
			method,
			body
		});
	}

	handleResponse = async (response, method) => {
		if (this.handleUnauthorized(response)) return null;

		if (!response.ok) {
			await this.emitHttpError(response, method);
			throw new Error(`HTTP  ${response.status}`);
		}

		try {
			return await response.json();
		} catch {
			return null;
		}
	}

	request = async (uri, method, data) => {
		const response = await fetch(uri, {
			method,
			mode: "cors",
			cache: "no-cache",
			credentials: "same-origin",
			headers: this.getHeaders(),
			redirect: "follow",
			referrerPolicy: "no-referrer",
			body: data ? JSON.stringify(data) : null
		});

		return await this.handleResponse(response, method);
	}

	stringResponseRequest = async (uri, method, data) => {
		const response = await fetch(uri, {
			method,
			mode: "cors",
			cache: "no-cache",
			credentials: "same-origin",
			headers: this.getHeaders(),
			redirect: "follow",
			referrerPolicy: "no-referrer",
			body: data ? JSON.stringify(data) : null
		});

		if (this.handleUnauthorized(response)) return null;

		if (!response.ok) {
			await this.emitHttpError(response, method);
			throw new Error(`HTTP  ${response.status}`);
		}

		return await response.text();
	}

	create = async (subsection, data, serviceUri) => {
		const service = this.getService(serviceUri);

		const response = await fetch(service + "/" + subsection, {
			method: "POST",
			mode: "cors",
			cache: "no-cache",
			credentials: "same-origin",
			headers: this.getHeaders(),
			body: JSON.stringify(data)
		});

		return await this.handleResponse(response, "POST");
	}

	read = async (subsection, id, serviceUri) => {
		const service = this.getService(serviceUri);

		const response = await fetch(service + "/" + subsection + "/" + id, {
			method: "GET",
			mode: "cors",
			cache: "no-cache",
			credentials: "same-origin",
			headers: this.getHeaders()
		});

		if (this.handleUnauthorized(response)) return null;

		if (!response.ok) {
			await this.emitHttpError(response, "GET");
			throw new Error(`HTTP  ${response.status}`);
		}

		return await response.json();
	}

	count = async (subsection, filter) => {
		const response = await fetch(getServiceUri() + "/" + subsection + "/count", {
			method: "POST",
			mode: "cors",
			cache: "no-cache",
			credentials: "same-origin",
			headers: this.getHeaders(),
			body: JSON.stringify(filter ? filter : {})
		});

		return await this.handleResponse(response, "POST");
	}

	getAttachment = async (subsection, id) => {
		const response = await fetch(getServiceUri() + "/" + subsection + "-attachment/" + id, {
			method: "GET",
			mode: "cors",
			cache: "no-cache",
			credentials: "same-origin",
			headers: this.getHeaders()
		});

		if (this.handleUnauthorized(response)) return null;

		if (!response.ok) {
			await this.emitHttpError(response, "GET");
			throw new Error(`HTTP  ${response.status}`);
		}

		return response;
	}

	upload = async (subsection, id, file) => {
		const formData = new FormData();
		formData.append("file", file);

		const response = await fetch(getServiceUri() + "/" + subsection + "/" + id, {
			method: "POST",
			mode: "cors",
			cache: "no-cache",
			credentials: "same-origin",
			headers: this.getHeaders(false),
			body: formData
		});

		return await this.handleResponse(response, "POST");
	}

	search = async (subsection, filter, serviceUri) => {
		const service = this.getService(serviceUri);

		const response = await fetch(service + "/" + subsection + "/search", {
			method: "POST",
			mode: "cors",
			cache: "no-cache",
			credentials: "same-origin",
			headers: this.getHeaders(),
			body: JSON.stringify(filter ? filter : {})
		});

		return await this.handleResponse(response, "POST");
	}

	list = async (subsection, filter) => {
		const response = await fetch(getServiceUri() + "/" + subsection + "/list", {
			method: "POST",
			mode: "cors",
			cache: "no-cache",
			credentials: "same-origin",
			headers: this.getHeaders(),
			body: JSON.stringify(filter ? filter : {})
		});

		return await this.handleResponse(response, "POST");
	}
}

export const rest = new RestClient();